/**
 * Browser audio utility functions for recording and validation
 */

import { translate, type SupportedLanguage } from '../lib/i18n';

/**
 * Validate audio file before upload
 */
export const validateAudioFile = (
  file: Blob,
  language: SupportedLanguage,
  maxSize: number = 10 * 1024 * 1024 // 10MB default
): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: translate('validation.audioFileTooLarge', language),
    };
  }

  // Check if file has content
  if (file.size < 100) {
    return {
      valid: false,
      error: translate('validation.audioFileEmpty', language),
    };
  }

  return { valid: true };
};

/**
 * Get browser-specific MIME type for recording
 */
export const getBrowserOptimalMimeType = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    // Safari
    return 'audio/mp4';
  }

  // Chrome, Firefox, Edge
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
    return 'audio/webm;codecs=opus';
  }

  if (MediaRecorder.isTypeSupported('audio/webm')) {
    return 'audio/webm';
  }

  return 'audio/mp4'; // Fallback
};

/**
 * Check if audio recording is supported in current browser
 */
export const isAudioRecordingSupported = (): boolean => {
  return !!(
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
};

export default {
  validateAudioFile,
  getBrowserOptimalMimeType,
  isAudioRecordingSupported,
};
